import React, { useEffect, useState } from 'react'
import { Container, Dropdown, Radio, Button, DatePicker } from 'components/UIElements'
import '../stylesheets/participant.scss'
import { MODAL_BUTTONS_MAP, MODAL_CLASSES_MAP, MODAL_CONTENT_MAP, DATE_FORMAT_MAP } from 'utils/constants'
import STRINGS from 'utils/strings'
import moment from 'moment'

const VISIT_CYCLE_OPTIONS = {
  currentCycle: 'currentCycle',
  nextCycle: 'nextCycle',
}

const ParticipantVisitCycle = props => {
  const {
    isPtpDisable = false,
    participantVisitCycles,
    baseSiteID,
    checkedCohorts,
    checkedSites,
    consentRequired,
    participant,
    participantDataFields,
    ptpSiteID,
    saveToDatabase,
    study,
    fetchPtp,
    studyVisitSchedule,
    studyID,
    fetchParticipantVisits,
    openModal,
    initializeCheckedCohorts,
    isReadOnlyState,
  } = props
  const [visitCycleOption, setVisitCycleOption] = useState()
  const [visitNextDate, setVisitNextDate] = useState(moment())

  const [completedVisitCycle, setCompletedVisitCycle] = useState(false)
  const [selectedVisitCycle, setSelectedVisitCycle] = useState()
  const [placeholder, setPlaceholder] = useState('Select next cycle')

  const checkIsDropdownDisabled = () => {
    const activeCycleWithNextCycleId = participantVisitCycles?.find(cycle => cycle?.is_active && !!cycle?.next_cycle_id)
    return !!activeCycleWithNextCycleId
  }

  const isDropdownDisabled = checkIsDropdownDisabled()

  const getAllCycles = () => {
    const currentParticipantArm = studyVisitSchedule?.find(item => item.id === participant?.study_schedule_id)

    const allPhases = studyVisitSchedule?.reduce((acc, arm) => {
      return [...acc, ...arm?.phases]
    }, [])

    const phasesArray = currentParticipantArm ? currentParticipantArm?.phases : allPhases

    const allCycles = phasesArray?.reduce((acc, phase) => {
      return [...acc, ...phase?.cycles]
    }, [])

    return allCycles
  }

  const getFormattedVisitCycles = () => {
    const allCycles = getAllCycles()

    const filteredCycles = allCycles?.filter(cycle => {
      const ptpVisitCycle = participantVisitCycles?.find(visitCycle => visitCycle?.id === cycle?.id)
      return !ptpVisitCycle
    })

    const formattedCycles = filteredCycles.map(cycle => {
      return {
        ...cycle,
        key: cycle.id,
        value: cycle.id,
        text: cycle.name,
      }
    })

    return formattedCycles
  }

  const formattedAllCycles = getFormattedVisitCycles()

  const getCurrentVisitCycle = () => {
    return participantVisitCycles?.find(cycle => cycle.is_active)
  }

  const currentVisitCycle = getCurrentVisitCycle()

  const getAvailableVisitCycles = () => {
    const index = formattedAllCycles.findIndex(item => item.id === currentVisitCycle?.cycle_id)
    const allFutureCycles = formattedAllCycles.slice(index + 1, formattedAllCycles?.length)

    return allFutureCycles
  }

  const availableVisitCycles = getAvailableVisitCycles()

  useEffect(() => {
    if (currentVisitCycle?.metadata?.cycle_start_ui) {
      setVisitNextDate(moment(currentVisitCycle?.metadata?.cycle_start_ui))
    } else if (currentVisitCycle?.cycle_start) {
      setVisitNextDate(moment(currentVisitCycle?.cycle_start))
    }
  }, [currentVisitCycle?.metadata?.cycle_start_ui, currentVisitCycle?.cycle_start])

  useEffect(() => {
    if (!!currentVisitCycle?.next_cycle_id) {
      setCompletedVisitCycle(true)

      const participantNextCycle = formattedAllCycles?.find(item => item?.id === currentVisitCycle?.next_cycle_id)

      if (participantNextCycle) {
        setSelectedVisitCycle(participantNextCycle)
      }
    }
  }, [currentVisitCycle])

  useEffect(() => {
    if (!visitCycleOption) {
      if (currentVisitCycle?.next_cycle_id === currentVisitCycle?.cycle_id) {
        setVisitCycleOption(VISIT_CYCLE_OPTIONS.currentCycle)
      } else {
        if (!availableVisitCycles?.length) {
          setVisitCycleOption(VISIT_CYCLE_OPTIONS.currentCycle)
        } else {
          setVisitCycleOption(VISIT_CYCLE_OPTIONS.nextCycle)
        }
      }
    }
  }, [visitCycleOption, availableVisitCycles?.length, currentVisitCycle])

  useEffect(() => {
    if (participant.study_groups) {
      initializeCheckedCohorts(Object.keys(participant.study_groups))
    }
  }, [])

  const onUpdateVisitCycle = async () => {
    setPlaceholder(selectedVisitCycle?.name)

    await saveToDatabase({
      studyID: study.id,
      siteID: ptpSiteID != null ? ptpSiteID : baseSiteID,
      participant: {
        ...participant,
        visit_cycle: {
          is_completed: true,
          next_cycle_id:
            visitCycleOption === VISIT_CYCLE_OPTIONS.currentCycle
              ? currentVisitCycle?.cycle_id
              : selectedVisitCycle?.id,
          next_cycle_start: moment(visitNextDate).format(DATE_FORMAT_MAP.datePickerWithFullTime),
        },
        is_self_sign_on: false,
      },
      checkedCohorts,
      participantID: participant.id,
      consentRequired,
      sites: checkedSites,
      pDataFields: participantDataFields,
      isNotLeaving: true,
      hideSuccessMessage: false,
    })
    await fetchParticipantVisits(studyID, participant.id)
    await fetchPtp(study.id, ptpSiteID, participant.id)
  }

  const selectionDisabled = isPtpDisable || isDropdownDisabled

  const checkIsDateFieldDisplayed = () => {
    if (!visitCycleOption) {
      return false
    }

    if (visitCycleOption === VISIT_CYCLE_OPTIONS.currentCycle) {
      return true
    }

    return selectedVisitCycle?.key
  }

  const isDateFieldDisplayed = checkIsDateFieldDisplayed()

  const checkIsSaveBtnDisabled = () => {
    if (!visitCycleOption) {
      return false
    }

    if (visitCycleOption === VISIT_CYCLE_OPTIONS.currentCycle) {
      return !visitNextDate
    }

    if (visitCycleOption === VISIT_CYCLE_OPTIONS.nextCycle) {
      return !visitNextDate || !selectedVisitCycle?.key
    }
  }

  const isSaveBtnDisabled = checkIsSaveBtnDisabled()

  const getDatepickerLabel = () => {
    const allCycles = getAllCycles()

    const currentVisitName =
      allCycles?.find(cycle => cycle?.id === currentVisitCycle?.cycle_id)?.visits?.[0]?.name || 'visit'

    if (visitCycleOption === VISIT_CYCLE_OPTIONS.currentCycle) {
      return `When should ${currentVisitCycle?.is_dummy ? 'visit' : currentVisitName} occur?`
    }

    const nextVisitName = selectedVisitCycle?.visits?.[0]?.name || 'visit'

    return `When should ${selectedVisitCycle?.is_dummy ? 'visit' : nextVisitName} occur?`
  }

  const datepickerLabel = getDatepickerLabel()

  if (!currentVisitCycle) {
    return null
  }

  return (
    <Container className='participant-visit-cycle'>
      <div className='title'>{STRINGS.visitCycle}</div>
      {!!currentVisitCycle ? <div>{`${STRINGS.ptpIsIn} ${currentVisitCycle.cycle_name}`}</div> : null}
      {!isReadOnlyState && (
        <>
          <>
            <Radio
              disabled={selectionDisabled}
              selected={visitCycleOption === VISIT_CYCLE_OPTIONS.currentCycle}
              onClick={() => {
                setVisitCycleOption(VISIT_CYCLE_OPTIONS.currentCycle)
                setVisitNextDate(moment())
              }}
              content={STRINGS.ptpWillRepeat}
            />
            {availableVisitCycles?.length ? (
              <Radio
                disabled={selectionDisabled}
                selected={visitCycleOption === VISIT_CYCLE_OPTIONS.nextCycle}
                onClick={() => {
                  setVisitCycleOption(VISIT_CYCLE_OPTIONS.nextCycle)
                  setVisitNextDate(undefined)
                }}
                content={STRINGS.ptpWillMove}
              />
            ) : null}
          </>

          {visitCycleOption ? (
            <div className='visit-cycle-option-content'>
              {visitCycleOption === VISIT_CYCLE_OPTIONS.nextCycle ? (
                <>
                  <p className='label-small'>{STRINGS.ptpWillProgress}</p>
                  <Dropdown
                    id='ptp-next-visit-cycle'
                    disabled={selectionDisabled}
                    selected={selectedVisitCycle?.key}
                    options={availableVisitCycles}
                    onSelect={item => {
                      setSelectedVisitCycle(item)
                      setVisitNextDate(moment())
                    }}
                    placeholder={placeholder}
                  />
                </>
              ) : null}

              {isDateFieldDisplayed ? (
                <div className='next-visit-day-box'>
                  <p className='label-small'>{datepickerLabel}</p>
                  <DatePicker
                    disabled={selectionDisabled}
                    disabledDays={{ before: new Date() }}
                    hasError={false}
                    date={moment(visitNextDate, DATE_FORMAT_MAP.datePickerWithFullTime)}
                    onDayChange={date => {
                      setVisitNextDate(date)
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <div className='submit-button-box'>
            <Button
              id='save-visit-cycle-option'
              className='save-visit-cycle-button'
              content={STRINGS.save}
              disabled={isSaveBtnDisabled}
              onClick={() => {
                openModal({
                  heading: MODAL_CONTENT_MAP.pleaseConfirm,
                  content: MODAL_CONTENT_MAP.participantNewCycle(participant?.id, currentVisitCycle?.cycle_name),
                  confirmButton: MODAL_BUTTONS_MAP.confirm,
                  cancelButton: MODAL_BUTTONS_MAP.cancel,
                  className: MODAL_CLASSES_MAP.confirmation,
                  onConfirm: () => {
                    onUpdateVisitCycle()
                  },
                })
              }}
            />
          </div>
        </>
      )}
    </Container>
  )
}

export default ParticipantVisitCycle
